import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { bindActionCreators } from "@reduxjs/toolkit";
import cn from "classnames";

import { type RootState, type AppDispatch } from "features/Application/globaltypes/redux";
import { TextTruncate } from "components";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import { Tooltip } from "components/common/tooltip";
import {
  GenericPerformanceListWithServerPagination,
  type ExportAvailability,
} from "features/Reporting/Content/shared/GenericPerformanceListWithServerPagination";
import { type ColumnOption } from "interfaces/columnOptions";
import { bindAction } from "interfaces";
import { setExportAction, setHasDataToExport } from "features/Reporting/state/export/exportSlice";
import { setIsReportEnabled, reset } from "features/Reporting/state/toolbar/toolbarSlice";
import { createAddOnsAccountsFetcher, useAddOnsEngagementQuery } from "./Queries/queries";
import { usePerformanceBreadcrumbs } from "features/Library/Common/Hooks/usePerformanceBreadcrumbs";

import styles from "./AddOnsReport.module.scss";
import CardReporting from "components/cards/CardReporting/CardReporting";
import ReportingFilter from "components/reportingFilter/ReportingFilter";
import { usePerformanceFilter } from "features/Library/Common/Hooks/usePerformanceFilter";
import { useAccountFilterShouldShow } from "features/Reporting/Content/queries/useAccountFilterShouldShow";
import ReportingExport from "components/reportingExport/ReportingExport";
import { ReportUnavailable } from "components/reportUnavailable/ReportUnavailable";
import { FeatureFlags } from "featureFlags";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import AddOnsReportDrilldown from "./AddOnsReportDrilldown";
import type { AddOnsAccountRow } from "../types/addOns";
import { doAddOnsAccountsExport, doAccountAddOnsExport } from "./Queries/exports";

export const AddOnsReport: FC<PropsFromRedux> = ({
  accountId,
  setExportAction,
  setHasExportData,
  setIsReportEnabled,
  resetIsReportEnabled,
  accountType,
  addOnsAccountsExport,
  accountAddOnsExport,
}) => {
  const reportEnabled = useFeatureFlag(FeatureFlags.AddOnsReport);
  const accountFilterShouldShow = useAccountFilterShouldShow(accountId, accountType, true);

  const [filter, setFilter] = usePerformanceFilter({
    accountId,
    includeAccountsDropdown: accountFilterShouldShow,
    includeAddons: true,
  });

  const [selectedAccount, setSelectedAccount] = useState<{ id: number; name: string } | null>(null);

  const { domElements, registerBreadcrumb, hasBreadcrumbsToDisplay } = usePerformanceBreadcrumbs();

  const engagementQuery = useAddOnsEngagementQuery(filter);

  const fetchAccountsPage = useMemo(() => createAddOnsAccountsFetcher(filter), [filter]);
  const queryKeyBase = useMemo(() => ["addOnsAccounts", filter] as const, [filter]);

  useEffect(() => {
    if (selectedAccount === null) {
      setExportAction({
        method: addOnsAccountsExport,
        args: [filter, "Add-Ons Metrics by Customer"],
        isExportEnabled: true,
      });
    } else {
      setExportAction({
        method: accountAddOnsExport,
        args: [selectedAccount.id, filter, `${selectedAccount.name} Add-Ons`],
        isExportEnabled: true,
      });
    }
  }, [selectedAccount, setExportAction, addOnsAccountsExport, accountAddOnsExport, filter]);

  const updateExportDataState = useCallback(
    ({ hasDataToExport }: ExportAvailability) =>
      setHasExportData({
        hasDataToExport,
        tooltipContent: "No data available for export. This Add-Ons report contains no data.",
      }),
    [setHasExportData],
  );

  useEffect(() => {
    setIsReportEnabled(reportEnabled);

    return () => {
      resetIsReportEnabled();
    };
  }, [reportEnabled, resetIsReportEnabled, setIsReportEnabled]);

  const handleAccountSelection = useCallback(
    (row: AddOnsAccountRow) => {
      setSelectedAccount({ id: row.CustomerId, name: row.CustomerName });
      registerBreadcrumb({
        text: row.CustomerName,
        action: () => {
          setSelectedAccount(null);
        },
      });
    },
    [registerBreadcrumb],
  );

  const columnOptions = useMemo<ColumnOption<AddOnsAccountRow>[]>(
    () => [
      {
        name: "Customer",
        width: 6,
        isSortable: true,
        sortKey: "CustomerName",
        render(entity) {
          return (
            <Tooltip
              target={
                <button className={styles.linkButton} onClick={() => handleAccountSelection(entity)}>
                  <TextTruncate className={styles.wordBreak}>{entity.CustomerName}</TextTruncate>
                </button>
              }
              content={entity.CustomerName}
              showAlways
            />
          );
        },
      },
      {
        name: "Active Add-Ons",
        width: 5,
        isSortable: true,
        sortKey: "ActiveAddons",
        render: ({ ActiveAddons }) => ActiveAddons.toLocaleString(),
      },
      {
        name: "Expired Add-Ons",
        width: 5,
        isSortable: true,
        sortKey: "ExpiredAddons",
        render: ({ ExpiredAddons }) => ExpiredAddons.toLocaleString(),
      },
    ],
    [handleAccountSelection],
  );

  const ignoreInCountAndHide = useMemo(
    () => (selectedAccount ? ["dates", "accounts", "includeMyData", "type"] : ["dates", "type"]),
    [selectedAccount],
  );

  if (!reportEnabled) {
    return (
      <div className={styles.reportUnavailable}>
        <ReportUnavailable />
      </div>
    );
  }

  return (
    <div className={"performanceRoot"}>
      <div className="performanceHeader">
        <div className={cn("performanceHeader", styles.header)}>
          {hasBreadcrumbsToDisplay ? (
            <div className="performanceBreadcrumbs">{domElements}</div>
          ) : (
            <h2 className="performanceTitle">Summary</h2>
          )}
          <div className="performanceActions">
            <ReportingFilter
              currentFilter={filter}
              callback={setFilter}
              includeAccountsDropdown={accountFilterShouldShow}
              includeAccounts={false}
              includeAddons
              ignoreInCountAndHide={ignoreInCountAndHide}
            />
            <ReportingExport />
          </div>
        </div>
      </div>
      <div className={styles.content}>
        {selectedAccount ? (
          <AddOnsReportDrilldown
            customerId={selectedAccount.id}
            filter={filter}
            hasDataForExport={updateExportDataState}
          />
        ) : (
          <>
            <div className={styles.cardsSection}>
              <RequestStatusRenderer state={engagementQuery.status}>
                {engagementQuery.isSuccess && engagementQuery.data && (
                  <CardReporting items={engagementQuery.data.cards} />
                )}
              </RequestStatusRenderer>
            </div>

            <div className={styles.tableSection}>
              <GenericPerformanceListWithServerPagination
                title="Metrics by Customer"
                columns={columnOptions}
                filter={filter}
                fetchPage={fetchAccountsPage}
                queryKeyBase={queryKeyBase}
                hasDataForExport={updateExportDataState}
                isDateAllTime
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  accountId: state.userProfile.accountId,
  accountType: state.userProfile.accountTypeId,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setExportAction: bindAction(setExportAction, dispatch),
  setHasExportData: bindAction(setHasDataToExport, dispatch),
  setIsReportEnabled: bindAction(setIsReportEnabled, dispatch),
  resetIsReportEnabled: bindAction(reset, dispatch),
  addOnsAccountsExport: bindActionCreators(doAddOnsAccountsExport, dispatch),
  accountAddOnsExport: bindActionCreators(doAccountAddOnsExport, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
export type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(AddOnsReport);
export default ConnectedComponent;
